import axios from "axios";
import useAuthStore from "../../services/authStore";
import "./MyAuthorisation.css";
import * as XLSX from "xlsx";
import pendingIcon from "../../assets/icons/warning.png";
import approvedIcon from "../../assets/icons/checkmark.png";
import rejectedIcon from "../../assets/icons/close.png";
import backIcon from "../../assets/icons/back.png";
import attachmentIcon from "../../assets/icons/attach.png";
import { toastSuccess, toastError } from "../../services/toaster";
import { useEffect, useState, useCallback, useRef } from "react";
import { toWords } from "number-to-words";
import pdfIcon from "../../assets/icons/pdf.png";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LuCornerUpLeft } from "react-icons/lu";
import { Eye, Download } from "lucide-react";
import { FaSpinner } from "react-icons/fa";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import editIcon from "../../assets/icons/edit.png";
import { FiRefreshCw } from "react-icons/fi";
const API_URL = process.env.REACT_APP_API_URL;

export default function MyAuthorisations() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [filteredRequests, setFilteredRequests] = useState([]); // ✅ ADD THIS LINE
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user_id);
  const companyId = useAuthStore((s) => s.companyId);
  const { status } = useParams();
  const [showDetail, setShowDetail] = useState(false);
  const searchMatchesRef = useRef([]);
  const selectedExpenseIdRef = useRef(null);
  const activeSearchMatchRef = useRef(-1);
  const goToSearchMatchRef = useRef(null);
  // const setRequestCounts = useAuthStore((s) => s.setRequestCounts);
  const setAuthCounts = useAuthStore((s) => s.setAuthCounts);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const searchFilters = useAuthStore((s) => s.searchFilters); // ✅ ADD THIS LINE
  const users = useAuthStore((s) => s.users);
  const [remarks, setRemarks] = useState({});
  const [attachmentLoading, setAttachmentLoading] = useState({});
  const formatCurrencyAmount = useAuthStore((s) => s.formatCurrencyAmount);
  const organizationLevels = useAuthStore((g) => g.organizationLevels);
  const [showFullRemarkHeader, setShowFullRemarkHeader] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(null);
  const getGradeDetails = (gradeId) => {
    return organizationLevels.find(
      (item) => String(item.id) === String(gradeId),
    );
  };
  const [previewType, setPreviewType] = useState(null); // "image" | "pdf" | "excel"
  const [excelData, setExcelData] = useState(null);
  const downloadExpensePdf = (company) => {
    if (!selected) return;

    const user = getUserDetails(selected.user_id);

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const leftMargin = 16;
    const rightMargin = 10;
    const pageWidth = 210;
    const pageHeight = 297;
    const bottomMargin = 10;

    let y = 12;

    // =========================
    // COMPANY HEADER
    // =========================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    doc.text(company?.company_name || "COMPANY", leftMargin, y);

    y += 6;

    doc.setFontSize(10);
    doc.text("Expenses Reimbursement Form", leftMargin, y);

    doc.setFont("helvetica", "normal");
    doc.text(
      `Expense ID : ${selected.expense_id || "-"}`,
      pageWidth - rightMargin,
      y,
      { align: "right" },
    );

    y += 8;

    // =========================
    // DATE + PURPOSE
    // =========================
    const expenseDate = selected.created_at
      ? new Date(selected.created_at).toLocaleDateString("en-GB")
      : "-";

    doc.setFontSize(10);

    doc.text(`Date : ${expenseDate}`, pageWidth - rightMargin, y, {
      align: "right",
    });

    const purposeWidth = 110;

    const purposeLines = doc.splitTextToSize(
      `Purpose: ${selected?.purpose || "-"}`,
      purposeWidth,
    );

    doc.text(purposeLines, leftMargin, y);

    y += purposeLines.length * 4 + 6;

    // =========================
    // HEADER TABLE
    // =========================
    autoTable(doc, {
      startY: y,
      theme: "grid",
      margin: {
        left: leftMargin,
        right: rightMargin,
      },

      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },

      body: [
        [
          "Name",
          user?.name || "-",
          "Employee Code",
          selected?.employee_code || user?.employee_code || "-",
        ],
        [
          "Designation",
          user?.designation_name || "-",
          "Department",
          user?.department_name || "-",
        ],
        ["Location", user?.location_name || "-", "", ""],
      ],

      columnStyles: {
        0: {
          cellWidth: 30,
          fillColor: [220, 230, 241],
          fontStyle: "bold",
        },
        1: {
          cellWidth: 69,
        },
        2: {
          cellWidth: 30,
          fillColor: [220, 230, 241],
          fontStyle: "bold",
        },
        3: {
          cellWidth: 55,
        },
      },
    });

    y = doc.lastAutoTable.finalY + 8;

    // =========================
    // TABLE DATA
    // =========================
    let serialNo = 1;
    const tableRows = [];
    const currencyTotals = {};

    Object.entries(groupedByCurrency).forEach(([currency, items]) => {
      const total = items.reduce(
        (sum, item) => sum + Number(item.total_amount || 0),
        0,
      );

      const approvedTotal = items.reduce(
        (sum, item) => sum + Number(item.approved_amount || 0),
        0,
      );

      currencyTotals[currency] = approvedTotal;

      // EXPENSE ROWS
      items.forEach((item) => {
        tableRows.push([
          serialNo++,
          item.date ? new Date(item.date).toLocaleDateString("en-GB") : "-",
          item.description || "-",
          item.expense_type_name || "-",
          currency,
          formatCurrencyAmount(Number(item.total_amount || 0), currency),
          item.approved_amount
            ? formatCurrencyAmount(Number(item.approved_amount), currency)
            : "-",
        ]);
      });

      // AMOUNT IN WORDS
      const amountInWords = toWords(Math.round(approvedTotal)).replace(
        /\b\w/g,
        (char) => char.toUpperCase(),
      );

      tableRows.push([
        {
          content: `Amount In Words : (${currency}) ${amountInWords} Only`,
          colSpan: 5,
          styles: {
            fillColor: [253, 233, 217],
            halign: "left",
            fontStyle: "normal",
            overflow: "linebreak",
            valign: "middle",
            cellPadding: 3,
          },
        },
        {
          content: formatCurrencyAmount(total, currency),
          styles: {
            fillColor: [253, 233, 217],
            fontStyle: "bold",
            halign: "right",
          },
        },
        {
          content: formatCurrencyAmount(approvedTotal, currency),
          styles: {
            fillColor: [253, 233, 217],
            fontStyle: "bold",
            halign: "right",
          },
        },
      ]);
    });

    // =========================
    // MAIN EXPENSE TABLE
    // =========================
    autoTable(doc, {
      startY: y,
      theme: "grid",

      margin: {
        left: leftMargin,
        right: rightMargin,
      },

      head: [
        [
          "SNO",
          "DATE",
          "DESCRIPTION",
          "TYPE",
          "CURRENCY",
          "AMOUNT",
          "APPROVED AMOUNT",
        ],
      ],

      body: tableRows,

      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
        overflow: "linebreak",
      },

      headStyles: {
        fillColor: [220, 230, 241],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        font: "helvetica",
      },

      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 18, halign: "center" },
        2: { cellWidth: 50, halign: "left" },
        3: { cellWidth: 27, halign: "left" },
        4: { cellWidth: 22, halign: "center" },
        5: { cellWidth: 28.5, halign: "right" },
        6: { cellWidth: 28.5, halign: "right" },
      },
    });

    // =========================
    // TOTAL - AFTER EXPENSE TABLE
    // =========================
    let totalY = doc.lastAutoTable.finalY + 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    const currencyEntries = Object.entries(currencyTotals);

    const totalsText =
      "Total :   " +
      currencyEntries
        .map(
          ([currency, amount]) =>
            `${currency} ${formatCurrencyAmount(amount, currency)}`,
        )
        .join("   |   ");

    doc.text(totalsText, leftMargin, totalY);

    // =========================
    // APPROVAL DATA
    // =========================
    const approvalSteps = (selected?.approvals || [])
      .slice()
      .sort((a, b) => (a.serial_no || 0) - (b.serial_no || 0));

    const formatApprovalDateTimePdf = (dateString) => {
      if (!dateString) return "-";

      const utcDate = new Date(
        dateString.endsWith("Z") ? dateString : dateString + "Z",
      );

      if (isNaN(utcDate.getTime())) return "-";

      const formattedDate = utcDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      });

      const formattedTime = utcDate.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });

      return `${formattedDate} | ${formattedTime}`;
    };

    // =========================
    // STATUS COLOR
    // =========================
    const getStatusColor = (status) => {
      const currentStatus = (status || "pending").toLowerCase();
    };

    // =========================
    // APPROVAL HEIGHT CALCULATION
    // =========================
    const stepsPerRow = 4;
    const approvalRows = Math.ceil(approvalSteps.length / stepsPerRow);

    const approvalSectionHeight =
      approvalSteps.length > 0 ? 8 + approvalRows * 28 + 10 : 0;

    const submittedSectionHeight = 10;

    // =========================
    // CHECK IF APPROVAL BLOCK FITS
    // =========================
    const requiredBottomSpace =
      approvalSectionHeight + submittedSectionHeight + bottomMargin;

    if (totalY + requiredBottomSpace > pageHeight) {
      doc.addPage();
    }

    // =========================
    // APPROVAL STATUS - AT BOTTOM
    // =========================
    let submittedY;

    if (approvalSteps.length > 0) {
      // Start calculation from bottom
      submittedY = pageHeight - bottomMargin;

      const approvalTableTotalHeight = approvalRows * 28;

      let approvalY =
        submittedY - submittedSectionHeight - approvalTableTotalHeight - 8;

      // APPROVAL TITLE
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);

      doc.text("APPROVAL STATUS", leftMargin, approvalY);

      approvalY += 5;

      // =========================
      // CREATE APPROVAL TABLE
      // =========================
      for (let i = 0; i < approvalSteps.length; i += stepsPerRow) {
        const chunk = approvalSteps.slice(i, i + stepsPerRow);

        // ROW 1: LEVEL NAME
        const headerRow = chunk.map((step) => {
          return step.level_name?.toLowerCase() === "initiated by"
            ? "Created By"
            : step.level_name || "Approver";
        });

        // ROW 2: NAME + STATUS
        const nameStatusRow = chunk.map((step) => {
          const approverUser = getUserDetails(step.user_id);

          const name = approverUser?.name || step.username || "-";

          const status = (step.status || "Pending").toUpperCase();

          return {
            content: `${name}\n${status}`,
            styles: {
              fillColor: getStatusColor(step.status),
              fontStyle: "bold",
              halign: "center",
              valign: "middle",
            },
          };
        });

        // ROW 3: DATE + TIME

        const dateTimeRow = chunk.map((step) => {
          const isCreatedBy =
            step.level_name?.toLowerCase() === "initiated by" ||
            Number(step.serial_no) === 1;

          const approvalDate = isCreatedBy
            ? selected.created_at
            : step.approved_at;

          return formatApprovalDateTimePdf(approvalDate);
        });

        autoTable(doc, {
          startY: approvalY,
          theme: "grid",

          margin: {
            left: leftMargin,
            right: rightMargin,
          },

          head: [headerRow],
          body: [nameStatusRow, dateTimeRow],

          styles: {
            font: "helvetica",
            fontSize: 8,
            cellPadding: 3,
            lineWidth: 0.3,
            lineColor: [0, 0, 0],
            textColor: [0, 0, 0],
            halign: "center",
            valign: "middle",
          },

          headStyles: {
            fillColor: [220, 230, 241],
            textColor: [0, 0, 0],
            fontStyle: "bold",
            halign: "center",
            valign: "middle",
          },

          bodyStyles: {
            fillColor: [255, 255, 255],
          },

          columnStyles: chunk.reduce((styles, _, index) => {
            styles[index] = {
              cellWidth: (pageWidth - leftMargin - rightMargin) / chunk.length,
            };

            return styles;
          }, {}),

          didParseCell: function (data) {
            // Keep date/time row white
            if (data.section === "body" && data.row.index === 1) {
              data.cell.styles.fillColor = [255, 255, 255];

              data.cell.styles.fontStyle = "normal";
            }
          },
        });

        approvalY = doc.lastAutoTable.finalY + 5;
      }

      // Submitted below the approval table
      submittedY = doc.lastAutoTable.finalY + 8;
    } else {
      // No approvals: Submitted By stays at bottom
      submittedY = pageHeight - bottomMargin;
    }

    // =========================
    // SUBMITTED BY + DATE
    // =========================
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    doc.text(`Submitted By: ${user?.name || "-"}`, leftMargin, submittedY);

    doc.text(`Date: ${expenseDate}`, pageWidth - rightMargin, submittedY, {
      align: "right",
    });

    // =========================
    // DIRECT DOWNLOAD PDF
    // =========================
    const fileName = `${selected.expense_id}.pdf`;

    doc.save(fileName);
  };
  const getUserDetails = (id) => {
    if (!Array.isArray(users)) return null;
    return users.find((u) => String(u.id) === String(id));
  };

  // ✅ ADD THIS
  const getStatusApproverName = (item) => {
    if (!Array.isArray(item?.approvals) || item.approvals.length === 0) {
      return null;
    }

    const status = item?.status?.toLowerCase();
    const sorted = [...item.approvals].sort(
      (a, b) => (a.serial_no ?? 0) - (b.serial_no ?? 0),
    );

    if (status === "pending") {
      const pending = sorted.filter(
        (a) => a.status?.toLowerCase() === "pending",
      );
      return pending.length > 0 ? pending[0].username : null;
    }

    if (status === "rejected") {
      const rejected = sorted.filter(
        (a) => a.status?.toLowerCase() === "rejected",
      );
      return rejected.length > 0 ? rejected[0].username : null;
    }

    if (status === "approved") {
      const approved = sorted.filter(
        (a) => a.status?.toLowerCase() === "approved",
      );
      return approved.length > 0
        ? approved[approved.length - 1].username
        : null;
    }

    return null;
  };
  const formatNotificationTime = () => {
    return new Date().toLocaleString("sv-SE", {
      timeZone: "Asia/Kolkata",
    });
  };
  const formatIndianDateTime = (dateString) => {
    if (!dateString) return "-";

    const utcDate = new Date(
      dateString.endsWith("Z") ? dateString : dateString + "Z",
    );

    return utcDate.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };
  const handleGlobalSearch = (filters) => {
    const { name, fromDate, toDate } = filters;

    let result = [...requests];

    // =========================
    // GLOBAL TEXT SEARCH
    // =========================
    if (name?.trim()) {
      const searchName = name.trim().toLowerCase();

      result = result.filter((item) => {
        const user = users.find((u) => String(u.id) === String(item.user_id));

        const searchableValues = [
          // Master expense
          item.expense_id,
          item.purpose,
          item.description,
          item.status,
          item.employee_code,
          item.currency,
          item.type,
          item.payment_ref_number,

          // Employee
          user?.name,
          user?.employee_code,
          user?.department_name,
          user?.designation_name,
          user?.location_name,

          // Approvals
          ...(item.approvals || []).flatMap((approval) => [
            approval.username,
            approval.user_id,
            approval.status,
            approval.level_name,
            approval.remarks,
            approval.department_name,
            approval.designation_name,
            approval.location_name,
          ]),

          // Expense lines
          ...(item.expense_lines || []).flatMap((line) => [
            line.description,
            line.expense_type_name,
            line.currency,
            line.date,
            line.total_amount,
            line.approved_amount,
            line.remark,
            line.remarks,
            line.gl_code,
          ]),

          // References
          ...(item.references || []).flatMap((reference) => [
            reference.reference_expense_code,
            reference.reference_total_amount,
            reference.reference_status,
          ]),
        ];

        return searchableValues.some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(searchName),
        );
      });
    }

    // =========================
    // FILTER BY FROM DATE
    // =========================
    if (fromDate) {
      const startDate = new Date(`${fromDate}T00:00:00`);

      result = result.filter((item) => {
        if (!item.created_at) return false;

        const expenseDate = new Date(item.created_at);

        return expenseDate >= startDate;
      });
    }

    // =========================
    // FILTER BY TO DATE
    // =========================
    if (toDate) {
      const endDate = new Date(`${toDate}T23:59:59`);

      result = result.filter((item) => {
        if (!item.created_at) return false;

        const expenseDate = new Date(item.created_at);

        return expenseDate <= endDate;
      });
    }

    setFilteredRequests(result);

    // =========================
    // CLEAR INVALID SELECTION
    // =========================
    if (
      selected &&
      !result.some((item) => Number(item.id) === Number(selected.id))
    ) {
      setSelected(null);
      setShowDetail(false);
    }
  };
  const clearSearchHighlights = () => {
    if (!window.CSS?.highlights) {
      return;
    }

    window.CSS.highlights.delete("global-search-highlight");
    window.CSS.highlights.delete("global-search-active");

    searchMatchesRef.current = [];
    activeSearchMatchRef.current = -1;
  };
  const applySearchHighlights = (searchText) => {
    clearSearchHighlights();

    if (!searchText?.trim()) {
      return;
    }

    if (!window.CSS?.highlights || !window.Highlight) {
      return;
    }

    const text = searchText.trim().toLowerCase();

    const containers = document.querySelectorAll(
      ".my-auth-left-panel, .my-auth-right-panel",
    );

    const matches = [];

    containers.forEach((container) => {
      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            if (!node.nodeValue?.trim()) {
              return NodeFilter.FILTER_REJECT;
            }

            const parent = node.parentElement;

            if (
              parent &&
              ["SCRIPT", "STYLE", "NOSCRIPT"].includes(parent.tagName)
            ) {
              return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
          },
        },
      );

      const textNodes = [];

      let node;

      while ((node = walker.nextNode())) {
        textNodes.push(node);
      }

      textNodes.forEach((textNode) => {
        const nodeText = textNode.nodeValue || "";
        const lowerText = nodeText.toLowerCase();

        let startIndex = 0;

        while (startIndex < lowerText.length) {
          const matchIndex = lowerText.indexOf(text, startIndex);

          if (matchIndex === -1) {
            break;
          }

          const range = new Range();

          range.setStart(textNode, matchIndex);

          range.setEnd(textNode, matchIndex + text.length);

          matches.push(range);

          startIndex = matchIndex + text.length;
        }
      });
    });

    searchMatchesRef.current = matches;

    if (!matches.length) {
      activeSearchMatchRef.current = -1;
      return;
    }

    // First result is active
    activeSearchMatchRef.current = 0;

    const allHighlight = new window.Highlight(...matches);

    window.CSS.highlights.set("global-search-highlight", allHighlight);

    const activeHighlight = new window.Highlight(matches[0]);

    window.CSS.highlights.set("global-search-active", activeHighlight);

    // Scroll to first match
    const firstElement = matches[0].startContainer.parentElement;

    if (firstElement) {
      firstElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  };
  const goToSearchMatch = (direction = 1) => {
    const matches = searchMatchesRef.current;

    if (!matches.length) {
      return;
    }

    let currentIndex = activeSearchMatchRef.current;

    if (currentIndex < 0) {
      currentIndex = direction === 1 ? 0 : matches.length - 1;
    } else {
      currentIndex += direction;

      if (currentIndex >= matches.length) {
        currentIndex = 0;
      }

      if (currentIndex < 0) {
        currentIndex = matches.length - 1;
      }
    }

    activeSearchMatchRef.current = currentIndex;

    if (window.CSS?.highlights && window.Highlight) {
      window.CSS.highlights.delete("global-search-active");

      const activeHighlight = new window.Highlight(matches[currentIndex]);

      window.CSS.highlights.set("global-search-active", activeHighlight);
    }

    const range = matches[currentIndex];

    const element = range.startContainer.parentElement;

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  };
  goToSearchMatchRef.current = goToSearchMatch;
  const sortedRequests = [...filteredRequests].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );
  const groupedRequests = sortedRequests.reduce((acc, item) => {
    const date = item.created_at?.split("T")[0] || "Unknown";

    if (!acc[date]) {
      acc[date] = [];
    }

    acc[date].push(item);
    return acc;
  }, {});
  const groupedByCurrency =
    selected?.expense_lines?.reduce((acc, item) => {
      const currency = item.currency || "NA";

      if (!acc[currency]) {
        acc[currency] = [];
      }

      acc[currency].push(item);
      return acc;
    }, {}) || {};
  // const generateIntegrationToken = useAuthStore(
  //   (s) => s.generateIntegrationToken,
  // );
  const showApiError = (err, fallback = "Something went wrong") => {
    console.error(err);

    const message =
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.msg ||
      err?.message ||
      fallback;

    toastError(message);
  };
  const [activeTab, setActiveTab] = useState("details");
  const fetchData = useCallback(async () => {
    if (!userId || !companyId || !token) return;

    try {
      setLoading(true);
      const dashboardRes = await axios.get(
        `${API_URL}/dashboard/counts/${companyId}/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = dashboardRes.data?.data;
      const requestData = data?.my_approvals;

      setAuthCounts({
        all: requestData?.total || 0,
        pending: requestData?.pending || 0,
        approved: requestData?.approved || 0,
        rejected: requestData?.rejected || 0,
      });

      let ids = [];

      if (status === "pending") {
        ids = requestData?.pending_ids || [];
      } else if (status === "approved") {
        ids = requestData?.approved_ids || [];
      } else if (status === "rejected") {
        ids = requestData?.rejected_ids || [];
      } else {
        ids = [
          ...(requestData?.pending_ids || []),
          ...(requestData?.approved_ids || []),
          ...(requestData?.rejected_ids || []),
        ];
      }

      if (!ids.length) {
        setRequests([]);
        setFilteredRequests([]);
        setSelected(null);
        setShowDetail(false);
        setLoading(false);
        return;
      }

      const idsString = ids.join(",");

      const expenseRes = await axios.get(
        `${API_URL}/expense/${companyId}/by-ids?ids=${idsString}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const expenses = expenseRes.data?.data || [];
      console.log("🔍 GET API Response - First expense:", expenses[0]);
      console.log(
        "🔍 Expense Lines with approved_amount:",
        expenses[0]?.expense_lines,
      );
      setRequests(expenses);
      setFilteredRequests(expenses); // ✅ ADD THIS LINE

      // ✅ Check if we have a selectedExpenseId from navigation state
      const selectedExpenseId =
        location.state?.selectedExpenseId || selectedExpenseIdRef.current;
      if (selectedExpenseId) {
        const selectedItem = expenses.find(
          (item) => Number(item.id) === Number(selectedExpenseId),
        );

        if (selectedItem) {
          selectedExpenseIdRef.current = selectedItem.id;
          setSelected(selectedItem);
          setShowDetail(true);
        } else {
          setSelected(null);
          setShowDetail(false);
        }
      } else {
        // ✅ Only reset selected if we don't have a selectedExpenseId
        if (!location.state?.selectedExpenseId) {
          setSelected(null);
          setShowDetail(false);
        }
      }
    } catch (err) {
      showApiError(err, "Failed to fetch approvals");
      setSelected(null);
    } finally {
      setLoading(false);
    }
  }, [userId, companyId, token, status, setAuthCounts, location.state]); // ✅ Add location.state to dependencies

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const formatApprovalDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(
      dateString.endsWith("Z") ? dateString : dateString + "Z",
    );

    return {
      date: date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      }),
      time: date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      }),
    };
  };
  const handleApproval = async (step, status) => {
    const currentRemark = remarks[selected?.id] || "";

    if (status === "Rejected" && !currentRemark.trim()) {
      toastError("Enter Remark");
      return;
    }

    // ✅ Check editable user and approved amount
    if (status === "Approved") {
      const currentApproval = selected?.approvals?.find(
        (a) => Number(a.id) === Number(step.id),
      );

      const isEditable = Number(currentApproval?.isedituser) === 1;

      const hasEmptyApprovedAmount = selected?.expense_lines?.some(
        (line) =>
          line.approved_amount === null ||
          line.approved_amount === "" ||
          Number(line.approved_amount) <= 0,
      );

      if (isEditable && hasEmptyApprovedAmount) {
        toastError("Fill approved amount");
        return;
      }
    }

    const payload = {
      expense_id: selected?.id,
      approval_id: step?.id,
      user_id: step?.user_id,
      status: status,
      remarks: currentRemark,
    };

    try {
      const res = await axios.patch(`${API_URL}/expense/approval`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          company_id: companyId,
        },
      });
      const notificationData = res?.data?.data?.notification;

      if (notificationData) {
        await sendNotification(notificationData);
      }
      if (res.data?.detail) {
        toastError(res.data.detail);
        return;
      }

      toastSuccess(`Expense ${status.toLowerCase()} successfully`);

      setRemarks({
        ...remarks,
        [selected?.id]: "",
      });
      await fetchData();
    } catch (err) {
      console.error(err);

      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Something went wrong";

      toastError(errorMsg);
    }
  };
  useEffect(() => {
    const initUsers = async () => {
      const store = useAuthStore.getState();
      await store.getUsers();
    };

    if (!users.length) {
      initUsers();
    }
  }, [users.length]);
  useEffect(() => {
    const handleSearchEnter = (event) => {
      const direction = event.detail?.direction === -1 ? -1 : 1;

      if (goToSearchMatchRef.current) {
        goToSearchMatchRef.current(direction);
      }
    };

    window.addEventListener("global-search-enter", handleSearchEnter);

    return () => {
      window.removeEventListener("global-search-enter", handleSearchEnter);
    };
  }, []);
  useEffect(() => {
    const searchText = searchFilters?.name?.trim() || "";

    const timer = setTimeout(() => {
      applySearchHighlights(searchText);
    }, 100);

    return () => {
      clearTimeout(timer);
      clearSearchHighlights();
    };
  }, [searchFilters?.name, filteredRequests, selected, activeTab]);
  useEffect(() => {
    handleGlobalSearch(searchFilters);
  }, [searchFilters, requests]);

  useEffect(() => {
    // Preserve the selected expense while refreshing the list.
    if (!location.state?.selectedExpenseId && !selectedExpenseIdRef.current) {
      setSelected(null);
      setShowDetail(false);
    }
  }, [requests, location.state?.selectedExpenseId]);

  const canTakeAction = (step, allSteps) => {
    const currentUserId = useAuthStore.getState().user_id;

    if (String(step.user_id) !== String(currentUserId)) return false;

    if (step.status?.toLowerCase() !== "pending") return false;

    const sorted = [...allSteps].sort((a, b) => a.serial_no - b.serial_no);

    const index = sorted.findIndex((s) => String(s.id) === String(step.id));

    if (index === -1) return false;

    if (index === 0) return true;

    const prevStep = sorted[index - 1];

    return prevStep?.status?.toLowerCase() === "approved";
  };
  const formatDateHeader = (dateString) => {
    if (!dateString || dateString === "Unknown") return dateString;

    const date = new Date(dateString + "T00:00:00");

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };
  // ================= CAN EDIT EXPENSE =================

  const canEditExpense = () => {
    if (!selected) return false;

    // Only pending expenses can be edited
    if (selected.status?.toLowerCase() !== "pending") {
      return false;
    }

    const approvals = selected.approvals || [];
    const currentUserId = useAuthStore.getState().user_id;

    // Do not allow editing if an approval has been rejected
    const hasRejectedApproval = approvals.some(
      (approval) => approval.status?.toLowerCase() === "rejected",
    );

    if (hasRejectedApproval) return false;

    // Sort approval steps in workflow order
    const sortedApprovals = [...approvals].sort(
      (a, b) => Number(a.serial_no || 0) - Number(b.serial_no || 0),
    );

    // Find the current user's approval step
    const currentUserApproval = sortedApprovals.find(
      (approval) => String(approval.user_id) === String(currentUserId),
    );

    // The logged-in user must have edit permission
    if (Number(currentUserApproval?.isedituser) !== 1) {
      return false;
    }

    // The user's approval must be pending
    if (currentUserApproval.status?.toLowerCase() !== "pending") {
      return false;
    }

    // Only allow editing when the user's approval step is actionable
    const currentIndex = sortedApprovals.findIndex(
      (approval) => String(approval.id) === String(currentUserApproval.id),
    );

    if (currentIndex === -1) return false;

    if (currentIndex > 0) {
      const previousApproval = sortedApprovals[currentIndex - 1];

      if (previousApproval.status?.toLowerCase() !== "approved") {
        return false;
      }
    }

    return true;
  };
  // ================= REPOST EXPENSE TO SAP =================
  const handleRepostToSap = async () => {
    if (!selected?.id || !companyId || !token) {
      toastError("Expense details are missing.");
      return;
    }

    // Allow repost only for approved expenses.
    if (selected.status?.toLowerCase() !== "approved") {
      toastError("Only approved expenses can be reposted.");
      return;
    }

    // Check whether the current user has edit permission.
    const currentUserId = useAuthStore.getState().user_id;

    const currentUserApproval = (selected.approvals || []).find(
      (approval) => String(approval.user_id) === String(currentUserId),
    );

    const role = String(useAuthStore.getState().role || "")
      .trim()
      .toLowerCase();

    const isAdmin = role === "admin";

    const isEditUser = Number(currentUserApproval?.isedituser) === 1;

    if (!isAdmin && !isEditUser) {
      toastError("You do not have permission to repost this expense.");
      return;
    }
    // Repost only when Draft Entry is empty.
    const draftEntry = selected.erp_docentry;

    if (
      draftEntry !== null &&
      draftEntry !== undefined &&
      String(draftEntry).trim() !== ""
    ) {
      toastError("This expense already has a Draft Entry.");
      return;
    }

    try {
      setApprovalLoading("repost");

      const response = await axios.post(
        `${API_URL}/expense/${companyId}/sap-repost/${selected.id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            force: false,
          },
        },
      );

      // Preserve the selected expense before refreshing.
      selectedExpenseIdRef.current = selected.id;

      toastSuccess(
        typeof response.data === "string"
          ? response.data
          : response.data?.message || "Expense reposted to SAP successfully.",
      );

      // Fetch the latest expense data and retain the current selection.
      await fetchData();
    } catch (err) {
      console.error("Repost to SAP API Error:", err);

      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to repost expense to SAP.";

      toastError(
        typeof errorMessage === "string"
          ? errorMessage
          : JSON.stringify(errorMessage),
      );
    } finally {
      setApprovalLoading(null);
    }
  };

  const handleSendBack = async (step) => {
    if (!selected?.id) {
      toastError("Expense not found.");
      return;
    }

    if (!step?.id) {
      toastError("Approval details not found.");
      return;
    }

    // Already sent back
    if (selected?.is_send_back === true) {
      return;
    }

    try {
      setApprovalLoading("send-back");

      const res = await axios.patch(
        `${API_URL}/expense/${companyId}/send-back/${selected.id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Get notification details from the Send Back API response,
      // following the same pattern as Approve and Reject.
      const notificationData = res?.data?.data?.notification;

      if (notificationData) {
        await sendNotification({
          ...notificationData,
          message:
            notificationData.message || "Your expense has been sent back.",
        });
      }

      if (res.data?.detail) {
        toastError(res.data.detail);
        return;
      }

      toastSuccess("Expense sent back successfully.");

      // Refresh the expense and its latest send-back status.
      await fetchData();
    } catch (err) {
      console.error("Send Back API Error:", err);

      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to send expense back.";

      toastError(errorMsg);
    } finally {
      setApprovalLoading(null);
    }
  };

  const handleAttachment = async (item, download = false) => {
    try {
      const expense = selected;
      const fileName = item.attachment.split("/").pop();
      const actionKey = `${item.id}-${download ? "download" : "view"}`;

      setAttachmentLoading((prev) => ({
        ...prev,
        [actionKey]: true,
      }));

      const res = await axios.get(`${API_URL}/expense/attachment`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          company_id: companyId,
          master_id: expense.id,
          expense_code: expense.expense_id,
          file_name: fileName,
          download: download,
        },
        responseType: "blob",
      });

      const fileURL = window.URL.createObjectURL(res.data);
      const mime = res.data.type;
      const lowerName = fileName.toLowerCase();

      const isPdf = mime.includes("pdf") || lowerName.endsWith(".pdf");
      const isExcel =
        mime.includes("spreadsheet") ||
        mime.includes("excel") ||
        lowerName.endsWith(".xlsx") ||
        lowerName.endsWith(".xls");

      // ================= VIEW =================
      if (!download) {
        if (isPdf) {
          setPreviewType("pdf");
          setPreviewUrl(fileURL);
          setShowPreview(true);
        } else if (isExcel) {
          const arrayBuffer = await res.data.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          setExcelData(rows);
          setPreviewType("excel");
          setShowPreview(true);
        } else {
          setPreviewType("image");
          setPreviewUrl(fileURL);
          setShowPreview(true);
        }
        return;
      }

      // ================= DOWNLOAD =================
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      toastError("Failed to load attachment");
    } finally {
      const actionKey = `${item.id}-${download ? "download" : "view"}`;

      setAttachmentLoading((prev) => ({
        ...prev,
        [actionKey]: false,
      }));
    }
  };
  const expenseTypeNameMap = {
    EC: " Expense Claim",
    PT: "Petty Cash",
    BT: "Advance Request",
    ECAA: "Advance Settlement",
    RAA: "Advance Refund",
  };
  if (loading) {
    return (
      <div className="my-auth-grade-limit-container">
        <div className="my-auth-center-loader">
          <FaSpinner className="my-auth-spinner" />
          <div>Loading...</div>
        </div>
      </div>
    );
  }
  const sendNotification = async (notificationData) => {
    try {
      const { integrationToken, integrationBaseUrl } = useAuthStore.getState();

      if (!integrationToken) {
        console.error("Integration token missing");
        return;
      }

      const params = new URLSearchParams();

      params.append("from_userid", notificationData.from_userid?.toString());

      params.append("to_userid", notificationData.to_userid?.toString());

      params.append("message", notificationData.message || "");

      params.append("link", notificationData.link || "");
      params.append(
        "timestamp",
        notificationData.timestamp || formatNotificationTime(),
      );

      const response = await axios.post(
        `${integrationBaseUrl}/api/notification_api`,
        params,
        {
          headers: {
            Authorization: `Bearer ${integrationToken}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );

      console.log("Notification sent", response.data);
    } catch (error) {
      console.error("Notification API failed", error);
    }
  };
  const handleApprovalWithLoader = async (step, status) => {
    try {
      setApprovalLoading(status === "Approved" ? "approve" : "reject");

      await handleApproval(step, status);
    } finally {
      setApprovalLoading(null);
    }
  };

  return (
    <div className="my-auth-container-fluid">
      <div className="row">
        {/* ================= LEFT PANEL ================= */}
        {/* LEFT PANEL */}
        <div
          className={`my-auth-col-12 my-auth-col-md-5 my-auth-left-panel ${
            showDetail ? "my-auth-hide-mobile" : ""
          }`}
        >
          {Object.keys(groupedRequests).length === 0 && !loading ? (
            <div className="my-auth-no-data-found">No Data Found</div>
          ) : (
            Object.entries(groupedRequests)
              .sort((a, b) => new Date(b[0]) - new Date(a[0]))
              .map(([date, items]) => (
                <div key={date}>
                  {/* DATE HEADER */}
                  <div className="my-auth-date-header">
                    {formatDateHeader(date)}
                  </div>
                  <div className="my-auth-card-list">
                    {items.map((item) => {
                      const user = getUserDetails(item.user_id);

                      const name = user?.name || "N/A";
                      const dept = user?.department_name || "N/A";
                      const empCode = item?.employee_code || "-";
                      const initials = name
                        .split(" ")
                        .map((n) => n[0])
                        .slice(0, 2)
                        .join("")
                        .toUpperCase();

                      return (
                        <div
                          key={item.id}
                          className={`my-auth-task-card ${
                            selected?.id === item.id
                              ? "my-auth-active-ticket"
                              : ""
                          }`}
                          onClick={() => {
                            selectedExpenseIdRef.current = item.id;
                            setSelected(item);
                            setShowDetail(true);
                          }}
                        >
                          {/* ROW 1: EXPENSE TYPE + EXPENSE ID */}
                          <div className="my-auth-expense-type-row">
                            <div className="my-auth-expense-type">
                              {expenseTypeNameMap[item?.type] ||
                                item?.type ||
                                "-"}
                            </div>

                            <span className="my-auth-sepp">|</span>

                            <span className="my-auth-expense-id">
                              {item?.expense_id || "-"}
                            </span>
                          </div>

                          {/* ROW 2: EXISTING CARD CONTENT */}
                          <div className="my-auth-card-container">
                            {/* AVATAR */}
                            <div className="my-auth-avatar">{initials}</div>

                            {/* CONTENT */}
                            <div className="my-auth-card-content">
                              {/* EMPLOYEE DETAILS */}
                              <div className="my-auth-row1">
                                <span className="my-auth-emp">{empCode}</span>
                                <span className="my-auth-sep">|</span>

                                <span className="my-auth-name">{name}</span>
                                <span className="my-auth-sep">|</span>

                                <span className="my-auth-dept">{dept}</span>
                              </div>

                              {/* PURPOSE */}
                              <div className="my-auth-row2">
                                {item.purpose || "No Purpose"}
                              </div>

                              {/* AMOUNT: YOUR EXISTING CURRENCY LOGIC */}
                              <div className="my-auth-row3">
                                {Object.entries(
                                  item.expense_lines?.reduce((acc, line) => {
                                    const currency =
                                      line.currency || item.currency || "NA";

                                    if (!acc[currency]) {
                                      acc[currency] = 0;
                                    }

                                    acc[currency] += Number(
                                      line.total_amount || 0,
                                    );

                                    return acc;
                                  }, {}) || {},
                                ).map(([currency, total], index, arr) => (
                                  <div
                                    key={currency}
                                    className="my-auth-currency-wrapper"
                                  >
                                    <div className="my-auth-currency-chip">
                                      <span className="my-auth-currency-code">
                                        {currency}
                                      </span>

                                      <span className="my-auth-currency-value">
                                        {formatCurrencyAmount(total, currency)}
                                      </span>
                                    </div>

                                    {index !== arr.length - 1 && (
                                      <span className="my-auth-currency-separator">
                                        |
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* RIGHT SIDE STATUS: UNCHANGED */}

                            {/* RIGHT SIDE STATUS */}
                            <div className="my-auth-card-right">
                              <div
                                className={`my-auth-status-badge ${item.status?.toLowerCase()}`}
                              >
                                {item.status}
                              </div>

                              {["pending", "approved", "rejected"].includes(
                                item.status?.toLowerCase(),
                              ) && (
                                <div
                                  className={`my-auth-status-approver-name ${item.status?.toLowerCase()}`}
                                >
                                  {getStatusApproverName(item) || "-"}
                                </div>
                              )}

                              {item?.is_send_back === true && (
                                <div className="my-auth-send-back-status">
                                  <span className="my-auth-send-back-icon">
                                    <LuCornerUpLeft />
                                  </span>
                                  <span>Sent Back</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
          )}
        </div>

        <div
          className={`my-auth-col-12 my-auth-col-md-7 my-auth-right-panel ${
            showDetail ? "my-auth-show-mobile" : "my-auth-hide-mobile"
          }`}
        >
          {selected &&
            (() => {
              const user = getUserDetails(selected.user_id);
              const grade = getGradeDetails(selected.user_grade);

              return (
                <>
                  {showDetail && (
                    <div
                      className="my-auth-mobile-back"
                      onClick={() => setShowDetail(false)}
                    >
                      <button className="my-auth-mobile-back-btn">
                        <img
                          src={backIcon}
                          alt="back"
                          className="my-auth-back-icon"
                        />
                      </button>
                    </div>
                  )}
                  {/* ================= HEADER ================= */}
                  <div className="my-auth-expense-header">
                    <div className="my-auth-header-top">
                      <div className="my-auth-header-info">
                        {expenseTypeNameMap[selected?.type] ||
                          selected?.type ||
                          "-"}{" "}
                        <span>|</span>
                        {user?.name || "N/A"} <span>|</span>
                        {user?.department_name || "N/A"} <span>|</span>
                        {selected?.employee_code || "-"}
                      </div>
                      {/* ATTACHMENT - TOP RIGHT */}
                      <div className="my-auth-header-attachment">
                        {selected?.attachment && (
                          <>
                            <Eye
                              size={24}
                              className="my-auth-attachment-icon"
                              onClick={() => handleAttachment(selected, false)}
                            />

                            <Download
                              size={24}
                              className="my-auth-attachment-icon"
                              onClick={() => handleAttachment(selected, true)}
                            />
                          </>
                        )}
                      </div>
                    </div>

                    <div className="my-auth-header-sub">
                      <span
                        className={`my-auth-status-pill ${selected.status?.toLowerCase()}`}
                      >
                        {selected.status}
                      </span>

                      {["pending", "approved", "rejected"].includes(
                        selected.status?.toLowerCase(),
                      ) && (
                        <span
                          className={`my-auth-status-approver-name ${selected.status?.toLowerCase()}`}
                        >
                          {selected.status?.toLowerCase() === "pending" &&
                            "Waiting on: "}

                          {selected.status?.toLowerCase() === "approved" &&
                            "Approved by: "}

                          {selected.status?.toLowerCase() === "rejected" &&
                            "Rejected by: "}

                          {getStatusApproverName(selected) || "-"}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ================= TABS (ONLY UI, NO EXTRA CONTENT) ================= */}
                  <div className="my-auth-tabs-header">
                    <div className="my-auth-tabs">
                      <div
                        className={`my-auth-tab ${activeTab === "details" ? "my-auth-active" : ""}`}
                        onClick={() => setActiveTab("details")}
                      >
                        📄 Details
                      </div>

                      <div
                        className={`my-auth-tab ${activeTab === "lineItems" ? "my-auth-active" : ""}`}
                        onClick={() => setActiveTab("lineItems")}
                      >
                        🧾 Line Items ({selected?.expense_lines?.length || 0})
                      </div>

                      <div
                        className={`my-auth-tab ${activeTab === "workflow" ? "my-auth-active" : ""}`}
                        onClick={() => setActiveTab("workflow")}
                      >
                        🔄 Status ({selected?.approvals?.length || 0})
                      </div>
                    </div>

                    {/* ✅ Actions Container - Forces Edit + PDF side by side */}

                    <div className="my-auth-actions-wrapper">
                      {(() => {
                        const approvals = selected?.approvals || [];

                        const isSendBack =
                          selected?.is_send_back === true ||
                          selected?.is_send_back === 1 ||
                          selected?.is_send_back === "true";

                        const hasEditUser = approvals.some(
                          (approval) => Number(approval.isedituser) === 1,
                        );

                        // Preserve the existing send-back restriction
                        const isEditDisabled = isSendBack && hasEditUser;

                        // Apply the edit-permission logic as well
                        const canShowEdit = canEditExpense();

                        return (
                          canShowEdit && (
                            <button
                              className={`auth-edit-btn ${isEditDisabled ? "disabled" : ""}`}
                              disabled={isEditDisabled}
                              onClick={() => {
                                if (isEditDisabled) return;

                                navigate(`/edit-expense/${selected.id}`, {
                                  state: {
                                    expenseData: selected,
                                    selectedExpenseId: selected.id,
                                    approvalEdit: true,
                                    from: "authorisations",
                                    currentStatus: status,
                                  },
                                });
                              }}
                              title={
                                isEditDisabled
                                  ? "Editing is not allowed"
                                  : "Edit expense"
                              }
                            >
                              <img
                                src={editIcon}
                                alt="Edit"
                                className="auth-edit-icon"
                              />
                            </button>
                          )
                        );
                      })()}
                      {/* ================= REPOST TO SAP BUTTON ================= */}
                      {(() => {
                        const currentUserId = useAuthStore.getState().user_id;

                        const currentUserApproval = (
                          selected?.approvals || []
                        ).find(
                          (approval) =>
                            String(approval.user_id) === String(currentUserId),
                        );

                        const role = String(useAuthStore.getState().role || "")
                          .trim()
                          .toLowerCase();

                        const isAdmin = role === "admin";

                        const isEditUser =
                          Number(currentUserApproval?.isedituser) === 1;

                        const isApproved =
                          selected?.status?.toLowerCase() === "approved";

                        const draftEntry = selected?.erp_docentry;

                        const isDraftEntryEmpty =
                          draftEntry === null ||
                          draftEntry === undefined ||
                          String(draftEntry).trim() === "";

                        const canRepost =
                          (isAdmin || isEditUser) &&
                          isApproved &&
                          isDraftEntryEmpty;

                        if (!canRepost) {
                          return null;
                        }

                        return (
                          <button
                            type="button"
                            className="auth-repost-btn"
                            disabled={approvalLoading === "repost"}
                            onClick={handleRepostToSap}
                            title="Repost to SAP"
                          >
                            {approvalLoading === "repost"
                              ? "Reposting..."
                              : "Repost to SAP"}
                          </button>
                        );
                      })()}
                      {selected?.status?.toLowerCase() !== "withdraw" && (
                        <img
                          src={pdfIcon}
                          alt="Download PDF"
                          className="my-auth-pdf-download-icon"
                          onClick={async () => {
                            try {
                              const res = await axios.get(
                                `${API_URL}/settings/company/${companyId}`,
                                {
                                  headers: { Authorization: `Bearer ${token}` },
                                },
                              );

                              downloadExpensePdf(res.data);
                            } catch (err) {
                              toastError("Failed to load company details");
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {/* ================= TAB CONTENT (SCROLLABLE) ================= */}
                  <div className="my-auth-tab-content-wrapper">
                    {activeTab === "details" && (
                      <div className="my-auth-tab-content">
                        <h3 className="my-auth-section-title">
                          Expense Details
                        </h3>

                        <div className="my-auth-detail-grid">
                          <div className="my-auth-label">Expense ID:</div>
                          <div className="my-auth-value">
                            {selected.expense_id}
                          </div>

                          <div className="my-auth-label">Purpose:</div>

                          <div className="my-auth-value">
                            {selected.purpose}
                          </div>

                          <div className="my-auth-label">Created On:</div>
                          <div className="my-auth-value">
                            {formatIndianDateTime(selected.created_at)}
                          </div>
                          <div className="my-auth-label">Created By:</div>
                          <div className="my-auth-value">{user?.name}</div>

                          <div className="my-auth-label">Status:</div>
                          <div className="my-auth-value">{selected.status}</div>

                          <div className="my-auth-label">Draft Entry:</div>
                          <div className="my-auth-value">
                            {selected?.erp_docentry ?? "-"}
                          </div>

                          <div className="my-auth-label">Amount:</div>

                          <div className="my-auth-multi-currency-total">
                            {Object.entries(
                              selected?.expense_lines?.reduce((acc, line) => {
                                const currency = line.currency || "NA";

                                if (!acc[currency]) {
                                  acc[currency] = 0;
                                }

                                acc[currency] += Number(line.total_amount || 0);

                                return acc;
                              }, {}) || {},
                            ).map(([currency, total], index, arr) => (
                              <span key={currency}>
                                {currency}{" "}
                                {formatCurrencyAmount(total, currency)}
                                {index !== arr.length - 1 && " | "}
                              </span>
                            ))}
                          </div>
                          <div className="label">Description:</div>

                          <div className="my-auth-value">
                            {selected.description}
                          </div>
                          <div className="my-auth-label">
                            Payment Ref Number:
                          </div>
                          <div className="my-auth-value">
                            {selected?.payment_ref_number || "-"}
                          </div>
                        </div>

                        <hr />

                        {/* ================= EMPLOYEE DETAILS ================= */}
                        <h3 className="my-auth-section-title">
                          Employee Details
                        </h3>

                        <div className="my-auth-detail-grid">
                          <div className="my-auth-label">Name:</div>
                          <div className="my-auth-value">{user?.name}</div>

                          <div className="my-auth-label">Employee Code:</div>
                          <div className="my-auth-value">
                            {selected?.employee_code || "-"}
                          </div>

                          <div className="my-auth-label">Department:</div>
                          <div className="my-auth-value">
                            {user?.department_name}
                          </div>

                          <div className="my-auth-label">Designation:</div>
                          <div className="my-auth-value">
                            {user?.designation_name}
                          </div>

                          <div className="my-auth-label">Grade:</div>
                          <div className="my-auth-value">
                            {grade ? `${grade.title}` : "-"}
                          </div>

                          <div className="my-auth-label">Location:</div>
                          <div className="my-auth-value">
                            {user?.location_name}
                          </div>
                        </div>
                      </div>
                    )}
                    {activeTab === "lineItems" && (
                      <div className="my-auth-tab-content">
                        <h3 className="my-auth-section-title">
                          Line Items ({selected?.expense_lines?.length || 0})
                        </h3>
                        {/* ================= REFERENCE DETAILS ================= */}
                        {selected?.references?.length > 0 && (
                          <div className="my-auth-reference-section">
                            <div className="my-auth-reference-table-wrapper">
                              <table className="my-auth-reference-table">
                                <thead>
                                  <tr>
                                    <th>Expense Code</th>
                                    <th>Total Amount</th>
                                    <th>Reference Status</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {selected.references.map((reference) => (
                                    <tr key={reference.id}>
                                      <td>
                                        {reference.reference_expense_code ||
                                          "-"}
                                      </td>

                                      <td>
                                        {formatCurrencyAmount(
                                          Number(
                                            reference.reference_total_amount ||
                                              0,
                                          ),
                                          selected?.currency || "INR",
                                        )}
                                      </td>

                                      <td>
                                        <span
                                          className={`my-auth-reference-status ${
                                            reference.reference_status?.toLowerCase() ||
                                            ""
                                          }`}
                                        >
                                          {reference.reference_status || "-"}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        <div className="my-auth-line-table">
                          {/* ================= FIXED HEADER ================= */}
                          <div className="my-auth-line-item my-auth-line-header">
                            <div className="my-auth-line-number">#</div>

                            <div className="my-auth-line-center">
                              <div className="my-auth-line-top">
                                <div className="my-auth-line-field">
                                  <span>Category</span>
                                </div>
                                <div className="my-auth-line-field">
                                  <span>Description</span>
                                </div>
                              </div>

                              <div className="my-auth-line-bottom">
                                <div className="my-auth-line-field">
                                  <span>Date</span>
                                </div>
                                <div className="my-auth-line-field">
                                  <span>Requested</span>
                                </div>
                                <div className="my-auth-line-field">
                                  <span>Approved</span>
                                </div>
                                <div className="my-auth-line-field">
                                  <span>Remark</span>
                                </div>
                              </div>
                            </div>

                            <div className="my-auth-line-attachment">
                              <img src={attachmentIcon} alt="Attachment" />
                            </div>
                          </div>

                          {/* ================= DATA ROWS ================= */}
                          {(() => {
                            let serialNo = 1;

                            return Object.entries(groupedByCurrency).map(
                              ([currency, items]) => {
                                const total = items.reduce(
                                  (sum, item) =>
                                    sum + Number(item.total_amount || 0),
                                  0,
                                );
                                const totalApproved = items.reduce(
                                  (sum, item) =>
                                    sum + Number(item.approved_amount || 0),
                                  0,
                                );

                                return (
                                  <div key={currency}>
                                    {items.map((item) => (
                                      <div
                                        key={item.id}
                                        className="my-auth-line-item"
                                      >
                                        {/* SERIAL NUMBER */}
                                        <div className="my-auth-line-number">
                                          {serialNo++}
                                        </div>

                                        {/* CENTER CONTENT */}
                                        <div className="my-auth-line-center">
                                          {/* TOP: Category | Description */}
                                          <div className="my-auth-line-top">
                                            <div className="my-auth-line-field">
                                              <strong
                                                title={
                                                  item.expense_type_name ||
                                                  "N/A"
                                                }
                                              >
                                                {item.expense_type_name ||
                                                  "N/A"}
                                              </strong>
                                            </div>
                                            <div className="my-auth-line-field">
                                              <strong
                                                title={
                                                  item.description || "N/A"
                                                }
                                              >
                                                {item.description || "N/A"}
                                              </strong>
                                            </div>
                                          </div>

                                          {/* BOTTOM: Date | Requested | Approved | Remark */}
                                          <div className="my-auth-line-bottom">
                                            <div className="my-auth-line-field">
                                              <strong>
                                                {item.date
                                                  ? new Date(
                                                      item.date,
                                                    ).toLocaleDateString(
                                                      "en-GB",
                                                    )
                                                  : selected.created_at
                                                    ? new Date(
                                                        selected.created_at,
                                                      ).toLocaleDateString(
                                                        "en-GB",
                                                      )
                                                    : "N/A"}
                                              </strong>
                                            </div>

                                            <div className="my-auth-line-field">
                                              <strong>
                                                {formatCurrencyAmount(
                                                  item.total_amount,
                                                  currency,
                                                )}
                                              </strong>
                                            </div>

                                            <div className="my-auth-line-field">
                                              <strong
                                                style={{ color: "#059669" }}
                                              >
                                                {item.approved_amount ? (
                                                  formatCurrencyAmount(
                                                    Number(
                                                      item.approved_amount,
                                                    ).toFixed(2),
                                                    currency,
                                                  )
                                                ) : (
                                                  <span className="my-auth-text-muted">
                                                    -
                                                  </span>
                                                )}
                                              </strong>
                                            </div>

                                            <div className="my-auth-line-field">
                                              <strong
                                                title={
                                                  item.remark ||
                                                  item.remarks ||
                                                  "-"
                                                }
                                              >
                                                {item.remark ||
                                                  item.remarks ||
                                                  "-"}
                                              </strong>
                                            </div>
                                          </div>
                                        </div>

                                        {/* ATTACHMENT */}
                                        <div className="my-auth-line-attachment">
                                          {item.attachment ? (
                                            <div className="my-auth-attachment-actions">
                                              {attachmentLoading[
                                                `${item.id}-view`
                                              ] ? (
                                                <div className="my-auth-mini-loader"></div>
                                              ) : (
                                                <Eye
                                                  size={18}
                                                  strokeWidth={1.5}
                                                  className="my-auth-attachment-icon"
                                                  onClick={() =>
                                                    handleAttachment(
                                                      item,
                                                      false,
                                                    )
                                                  }
                                                />
                                              )}
                                              {attachmentLoading[
                                                `${item.id}-download`
                                              ] ? (
                                                <div className="my-auth-mini-loader"></div>
                                              ) : (
                                                <Download
                                                  size={18}
                                                  strokeWidth={1.5}
                                                  className="my-auth-attachment-icon"
                                                  onClick={() =>
                                                    handleAttachment(item, true)
                                                  }
                                                />
                                              )}
                                            </div>
                                          ) : (
                                            <span className="my-auth-text-muted">
                                              N/A
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ))}

                                    {/* CURRENCY SUBTOTAL */}
                                    <div className="my-auth-currency-total-simple">
                                      <span className="my-auth-total-title">
                                        Total ({currency})
                                      </span>
                                      <span className="my-auth-total-amount">
                                        Requested:{" "}
                                        {formatCurrencyAmount(total, currency)}
                                      </span>
                                      <span className="my-auth-total-separator">
                                        |
                                      </span>
                                      <span
                                        className="my-auth-total-amount"
                                        style={{ color: "#059669" }}
                                      >
                                        Approved:{" "}
                                        {formatCurrencyAmount(
                                          totalApproved.toFixed(2),
                                          currency,
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                );
                              },
                            );
                          })()}
                        </div>
                      </div>
                    )}
                    {activeTab === "workflow" && (
                      <div className="my-auth-tab-content">
                        <h3 className="my-auth-section-title">Status</h3>

                        <div className="my-auth-timeline">
                          {(selected?.approvals || [])
                            .slice()
                            .sort((a, b) => a.serial_no - b.serial_no)
                            .map((step, index, arr) => {
                              const status = (step.status || "").toLowerCase();

                              // Find rejected approval index
                              const rejectedIndex = arr.findIndex(
                                (item) =>
                                  (item.status || "").toLowerCase() ===
                                  "rejected",
                              );

                              // All approvals after rejected approval
                              const isAfterRejected =
                                rejectedIndex !== -1 && index > rejectedIndex;

                              const getStatusUI = (status) => {
                                switch (status) {
                                  case "pending":
                                    return {
                                      class: "pending",
                                      icon: pendingIcon,
                                    };

                                  case "approved":
                                    return {
                                      class: "approved",
                                      icon: approvedIcon,
                                    };

                                  case "rejected":
                                    return {
                                      class: "rejected",
                                      icon: rejectedIcon,
                                    };

                                  default:
                                    return {
                                      class: "pending",
                                      icon: pendingIcon,
                                    };
                                }
                              };

                              const ui = getStatusUI(status);

                              const name = step.username;
                              const dept = step.department_name;
                              const desig = step.designation_name;
                              const location = step.location_name;

                              return (
                                <div
                                  className={`my-auth-timeline-item ${
                                    isAfterRejected
                                      ? "my-auth-timeline-disabled"
                                      : ""
                                  }`}
                                  key={step.id}
                                >
                                  {/* LEFT SIDE */}
                                  <div className="my-auth-timeline-left">
                                    <div
                                      className={`my-auth-timeline-circle ${
                                        isAfterRejected ? "disabled" : ui.class
                                      }`}
                                    >
                                      <img
                                        src={ui.icon}
                                        alt="status"
                                        className="my-auth-timeline-icon"
                                      />
                                    </div>
                                  </div>

                                  <div className="my-auth-timeline-content">
                                    <div className="my-auth-wf-row-1">
                                      {step.level_name?.toLowerCase() ===
                                      "initiated by"
                                        ? "Created By"
                                        : step.level_name ||
                                          `Step ${index + 1}`}
                                    </div>
                                    <div className="my-auth-wf-row-2">
                                      <span>{name}</span>

                                      <span className="my-auth-separator">
                                        |
                                      </span>

                                      <span
                                        className={`my-auth-status ${ui.class}`}
                                      >
                                        {step.status}
                                      </span>

                                      {step.status?.toLowerCase() !==
                                        "pending" && (
                                        <>
                                          <span className="my-auth-separator">
                                            |
                                          </span>
                                          <span className="my-auth-approval-datetime">
                                            {
                                              formatApprovalDateTime(
                                                step.approved_at ||
                                                  (index === 0
                                                    ? selected.created_at
                                                    : null),
                                              ).date
                                            }
                                          </span>
                                          <span className="my-auth-separator">
                                            |
                                          </span>
                                          <span className="my-auth-approval-datetime">
                                            {
                                              formatApprovalDateTime(
                                                step.approved_at ||
                                                  (index === 0
                                                    ? selected.created_at
                                                    : null),
                                              ).time
                                            }
                                          </span>
                                        </>
                                      )}
                                      {step.remarks?.trim() && (
                                        <>
                                          <span className="my-auth-separator">
                                            |
                                          </span>

                                          <span className="my-auth-wf-remark">
                                            <strong>Remark:</strong>{" "}
                                            {step.remarks}
                                          </span>
                                        </>
                                      )}
                                    </div>

                                    <div className="my-auth-wf-row-3">
                                      <span>{dept}</span>
                                      <span className="my-auth-separator">
                                        |
                                      </span>
                                      <span>{desig}</span>
                                      <span className="my-auth-separator">
                                        |
                                      </span>
                                      <span>{location}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ================= STICKY ACTION BUTTONS ================= */}
                  {(() => {
                    const currentUserId = useAuthStore.getState().user_id;
                    const approvals = selected?.approvals || [];

                    const actionableStep = approvals
                      .slice()
                      .sort((a, b) => a.serial_no - b.serial_no)
                      .find((step, index, arr) => {
                        // Only current logged-in user's approval
                        if (String(step.user_id) !== String(currentUserId)) {
                          return false;
                        }

                        // Approval must be pending
                        if (step.status?.toLowerCase() !== "pending") {
                          return false;
                        }

                        // First approval step can take action directly
                        if (index === 0) {
                          return true;
                        }

                        // Previous approval must be approved
                        const prevStep = arr[index - 1];

                        return prevStep?.status?.toLowerCase() === "approved";
                      });

                    // No actionable pending approval = no buttons
                    if (!actionableStep) {
                      return null;
                    }

                    const isSendBack = Boolean(selected?.is_send_back);

                    return (
                      <div className="my-auth-sticky-actions">
                        <div className="my-auth-action-buttons">
                          {/* SEND BACK */}
                          <button
                            type="button"
                            className={`my-auth-send-back-btn ${
                              isSendBack ? "my-auth-send-back-disabled" : ""
                            }`}
                            onClick={() => {
                              if (isSendBack) {
                                return;
                              }

                              handleSendBack(actionableStep);
                            }}
                            disabled={isSendBack || approvalLoading !== null}
                          >
                            {approvalLoading === "send-back" ? (
                              <>
                                <span className="my-auth-btn-spinner"></span>
                                Sending Back...
                              </>
                            ) : isSendBack ? (
                              "Already Sent Back"
                            ) : (
                              "Send Back"
                            )}
                          </button>

                          {/* APPROVE */}
                          <button
                            type="button"
                            className={`my-auth-approve-btn ${
                              isSendBack ? "my-auth-action-disabled" : ""
                            }`}
                            onClick={() => {
                              if (isSendBack) return;

                              handleApprovalWithLoader(
                                actionableStep,
                                "Approved",
                              );
                            }}
                            disabled={isSendBack || approvalLoading !== null}
                          >
                            {approvalLoading === "approve" ? (
                              <>
                                <span className="my-auth-btn-spinner"></span>
                                Approving...
                              </>
                            ) : (
                              "Approve"
                            )}
                          </button>

                          {/* REJECT */}
                          <button
                            type="button"
                            className={`my-auth-reject-btn ${
                              isSendBack ? "my-auth-action-disabled" : ""
                            }`}
                            onClick={() => {
                              if (isSendBack) return;

                              handleApprovalWithLoader(
                                actionableStep,
                                "Rejected",
                              );
                            }}
                            disabled={isSendBack || approvalLoading !== null}
                          >
                            {approvalLoading === "reject" ? (
                              <>
                                <span className="my-auth-btn-spinner"></span>
                                Rejecting...
                              </>
                            ) : (
                              "Reject"
                            )}
                          </button>

                          {/* REMARK */}
                          <input
                            type="text"
                            className="my-auth-text-box"
                            placeholder="Enter Remark..."
                            value={remarks[selected?.id] || ""}
                            onChange={(e) =>
                              setRemarks({
                                ...remarks,
                                [selected?.id]: e.target.value,
                              })
                            }
                            disabled={approvalLoading !== null}
                          />
                        </div>
                      </div>
                    );
                  })()}
                  {showPreview && (
                    <div
                      className="my-auth-preview-overlay"
                      onClick={() => setShowPreview(false)}
                    >
                      <div
                        className={
                          previewType === "pdf" || previewType === "excel"
                            ? "my-auth-preview-box-pdf"
                            : "my-auth-preview-box"
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="my-auth-close-btn"
                          onClick={() => setShowPreview(false)}
                        >
                          ✕
                        </button>

                        {previewType === "pdf" && (
                          <iframe
                            src={previewUrl}
                            title="PDF Preview"
                            className="my-auth-preview-pdf"
                          />
                        )}

                        {previewType === "excel" && (
                          <div className="my-auth-excel-preview-wrapper">
                            <table className="my-auth-excel-preview-table">
                              <tbody>
                                {excelData?.map((row, rIdx) => (
                                  <tr key={rIdx}>
                                    {row.map((cell, cIdx) => (
                                      <td key={cIdx}>{cell}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {previewType === "image" && (
                          <img
                            src={previewUrl}
                            alt="preview"
                            className="my-auth-preview-img"
                          />
                        )}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
        </div>
      </div>
    </div>
  );
}
